<template>
  <div class="ej-home">
      <div class="intro-container">
        <div class="intro">
          <div class="intro-text-container">
            <h1 class="intro-header">
              Create and Share <br />Augmented Reality Art
            </h1>
            <p class="webar-tagline"><span class="webar-prefix">Now with</span> <strong>WebAR</strong></p>
            <h3 class="intro-copy">
              EyeJack Creator helps you bring art to life with animation and
              sound in 3 easy steps.
            </h3>
            <div class="download-button" id="desktop-download-button">
              <div class="download-button-wrapper">
                <a href="/login" target="_top">
                  <button class="primary">Launch Now</button>
                </a>
                <p class="download-links-top">
                  Or download legacy app:
                  <a :href="macDownloadUrl || '#'" :aria-disabled="!macDownloadUrl" :class="{ 'disabled-link': !macDownloadUrl }" target="_blank" rel="noopener noreferrer">Mac</a>
                  |
                  <a :href="winDownloadUrl || '#'" :aria-disabled="!winDownloadUrl" :class="{ 'disabled-link': !winDownloadUrl }" target="_blank" rel="noopener noreferrer">Win</a>
                </p>
              </div>
            </div>
          </div>
          <div class="intro-img-container">
            <!-- <img loading="lazy" decoding="async" src='../assets/hero-img-copy.png' alt="feature image"> -->
            <video
              autoplay
              loop
              muted
              playsinline
              poster="/landing/hero-poster.jpg"
              src="../assets/OriTor_Hero.mp4"
            ></video>
          </div>
          <div class="download-button" id="mobile-download-button">
            <div class="download-button-wrapper">
              <a href="/login" target="_top">
                <button class="primary">Launch Now</button>
              </a>
              <p class="download-links-top">
                Or download legacy app:
                <a :href="macDownloadUrl || '#'" :aria-disabled="!macDownloadUrl" :class="{ 'disabled-link': !macDownloadUrl }" target="_blank" rel="noopener noreferrer">Mac</a>
                |
                <a :href="winDownloadUrl || '#'" :aria-disabled="!winDownloadUrl" :class="{ 'disabled-link': !winDownloadUrl }" target="_blank" rel="noopener noreferrer">Win</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="steps" id="features">
        <div class="step" id="step-1">
          <div class="step-text-container">
            <h2 class="step-header-text">Add your artwork</h2>
            <h3>
              This is the image that will trigger your animation. Simply upload
              your artwork as either a JPG or PNG.
            </h3>
          </div>
          <div class="step-image-container">
            <img loading="lazy" decoding="async"
              class="step-image"
              id="step-1-3-image"
              src="../assets/step-1.png"
              alt="Upload your artwork — the image that triggers your AR"
            />
          </div>
        </div>
        <div class="step" id="step-2">
          <div class="step-text-container" id="step-2-text">
            <h2 class="step-header-text">Add your animation</h2>
            <h3>
              Choose a video file, GIF or PNG sequence that you want to play
              over your artwork.
            </h3>
          </div>
          <div class="step-image-container">
            <img loading="lazy" decoding="async"
              class="step-image"
              id="step-2-image"
              src="../assets/step-2.png"
              alt="Add your animation — a video, GIF or image sequence"
            />
          </div>
        </div>
        <div class="step" id="step-3">
          <div class="step-text-container">
            <h2 class="step-header-text">Preview your AR art</h2>
            <h3>
              Watch your augmented artwork come to life.
            </h3>
          </div>
          <div class="step-image-container">
            <img loading="lazy" decoding="async"
              class="step-image"
              id="step-1-3-image"
              src="../assets/step-3.gif"
              alt="Preview your AR artwork coming to life"
            />
          </div>
        </div>
      </div>

      <div class="demo-container" id="demo">
        <div class="demo-content">
          <div class="demo-img">
            <img loading="lazy" decoding="async"
              class="img-after"
              src="../assets/demo-artwork.png"
              style="max-width: 100%"
              alt=""
            />
          </div>

          <div class="demo-text">
            <div class="demo-title">
              <h2>Try WebAR Demo</h2>
              <h3>
                To view, open camera on smartphone and scan code.
              </h3>
            </div>
            <div class="demo-qr">
              <img loading="lazy" decoding="async"
                src="https://api.qrserver.com/v1/create-qr-code/?size=400x400&amp;data=https%3A%2F%2Flaunch.eyejack.io%2FArtwork-aa7f5a50-1c6f-4fd6-9b31-fc21f89cfb83"
                alt="QR code — scan with your phone to open the WebAR demo"
                style="border-radius: 8px; background: #ffffff; padding: 24px; box-sizing: border-box"
              />
            </div>
          </div>
          <h2 id="mobile-demo-title">Try WebAR Demo</h2>
        </div>
      </div>

      <div class="panels-container" id="panels">
        <div class="panel">
          <div class="panels-title">
            <h2>Panels</h2>
            <h3>
              Every place has a story and you can tell it with EyeJack Panels
            </h3>
          </div>
          <p
            class="keyline-after"
            style="margin-top: 10px; text-align: center"
          ></p>
          <div class="videos wrapper">
            <div class="video-row reverse">
              <div class="copy-container" id="copy-1">
                <h3>Humpback Whale Watching</h3>
                <p>
                  Panels is the easiest way to create and display AR content in
                  the real world. Using a combination of still and animated
                  panels arranged in 3D, it’s a great way to share a story at
                  any physical location.
                </p>
                <p>
                  Perfect as an AR solution for walks, trails and tours, across
                  public facing sectors such as Education, Tourism, Heritage,
                  Councils, Events and Retail.
                </p>
              </div>
              <div class="video-container">
                <!-- <img loading="lazy" decoding="async" src="../assets/whale-panels-1.png"> -->
                <video
                  autoplay
                  loop
                  muted
                  playsinline
                  src="../assets/whale-5-30.mp4"
                ></video>
              </div>
            </div>
          </div>
          <div class="videos wrapper">
            <div class="video-row">
              <div class="copy-container" id="copy-2">
                <h3>Make your own</h3>
                <p>
                  No 3D skills required! Simply add images and videos to your
                  scene and preview on your phone in seconds.
                </p>
              </div>
              <div class="video-container" id="whale-watching-2">
                <!-- <img loading="lazy" decoding="async" src="../assets/whale-watching-laptop.png"> -->
                <video
                  autoplay
                  loop
                  muted
                  playsinline
                  src="../assets/Whales.mp4"
                ></video>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- <div class="creators-container" id="creators">
        <div class="creator">
          <h2> Meet the Creators </h2>
          <p class="keyline-after" style="text-align: center;"></p>
          <div class="creators-table">
            <div class="creators-card" id="card-left">
              <img loading="lazy" decoding="async" src="../assets/creator-1.png">
              <div class="creators-text">
                <h3 class="creator-card-header">Nixi Killick</h3>
                <p class="artist-title">Artist</p>
                <p class="text-quote"><q>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus imperdiet
                    lorem at tempor consectetur.</q></p>
              </div>
            </div>
            <div class="creators-card" id="card-right">
              <img loading="lazy" decoding="async" src="../assets/creator-2.png">
              <div class="creators-text-right">
                <h3 class="creator-card-header">James Jirat</h3>
                <p class="artist-title">Designer</p>
                <p class="text-quote"><q>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus imperdiet
                    lorem at tempor consectetur.</q></p>
              </div>
            </div>
            <div class="creators-card" id="card-left">
              <img loading="lazy" decoding="async" src="../assets/creator-3.png">
              <div class="creators-text">
                <h3 class="creator-card-header">Bobby Jenkins</h3>
                <p class="artist-title">Student</p>
                <p class="text-quote"><q>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus imperdiet
                    lorem at tempor consectetur.</q></p>
              </div>
            </div>
            <div class="creators-card" id="card-right">
              <img loading="lazy" decoding="async" src="../assets/creator-4.png">
              <div class="creators-text-right">
                <h3 class="creator-card-header">Lady PheOnix</h3>
                <p class="artist-title">Curator of Galleries</p>
                <p class="text-quote"><q>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus imperdiet
                    lorem at tempor consectetur.</q></p>
              </div>
            </div>
            <div class="creators-card" id="card-left">
              <img loading="lazy" decoding="async" src="../assets/creator-5.png">
              <div class="creators-text">
                <h3 class="creator-card-header">Jane Ruby</h3>
                <p class="artist-title">Curator of Communities</p>
                <p class="text-quote"><q>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus imperdiet
                    lorem at tempor consectetur.</q></p>
              </div>
            </div>
            <div class="creators-card" id="card-right">
              <img loading="lazy" decoding="async" src="../assets/creator-6.png">
              <div class="creators-text-right">
                <h3 class="creator-card-header">Chris Bodem</h3>
                <p class="artist-title">Educator</p>
                <p class="text-quote"><q>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus imperdiet
                    lorem at tempor consectetur.</q></p>
              </div>
            </div>
          </div>
        </div>
      </div> -->

      <div class="pricing-container" id="pricing">
        <div class="pricing">
          <h2 class="pricing-title">Unlimited Free Artworks!</h2>
          <p class="pricing-title-sub">
            Take advantage of generous tiered features when you need them.
          </p>
          <div class="pricing-table">
            <div class="pricing-card">
              <h3 class="pricing-card-header">FREE</h3>
              <span class="keyline-after-card"></span>
              <ul>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />
                  Publish Artworks
                </li>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Collections
                </li>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Panels
                </li>
              </ul>
              <p><b>100</b> Free Views per month</p>
              <p class="card-sub-text">
                (Once Views run out, artworks are disabled until next monthly
                period.)
              </p>
              <p class="card-bottom">
                Up to <b>30</b> seconds per Artwork <br /><br />
                Up to <b>3</b> MB per Panels Project
              </p>
              <p class="pricing-button" id="free">FREE</p>
            </div>
            <div class="pricing-card">
              <h3 class="pricing-card-header">ARTIST</h3>
              <span class="keyline-after-card"></span>
              <ul>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Artworks
                </li>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Collections
                </li>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Panels
                </li>
              </ul>
              <p><b>10,000</b> Free Views per month</p>
              <p class="card-sub-text" style="padding-bottom: 15px">
                ($0.01 per View after 10,000)
              </p>
              <p class="card-bottom">
                Up to <b>60</b> seconds per Artwork <br /><br />
                Up to <b>20</b> MB per Panels Project
              </p>
              <div class="pricing-button-wrapper">
                <p class="pricing-button">$10/month</p>
                <div class="sub-pricing-button">(Pricing in USD)</div>
              </div>
            </div>
            <div class="pricing-card">
              <h3 class="pricing-card-header">VIRTUOSO</h3>
              <span class="keyline-after-card"></span>
              <ul>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Artworks
                </li>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Collections
                </li>
                <li>
                  <img loading="lazy" decoding="async"
                    src="../assets/tick.png" alt=""
                    class="icon-tick"
                    style="max-width: 4%"
                  />Publish Panels
                </li>
              </ul>
              <p><b>100,000</b> Free Views per month</p>
              <p class="card-sub-text" style="padding-bottom: 15px">
                ($0.01 per View after 100,000)
              </p>
              <p class="card-bottom">
                Up to <b>120</b> seconds per Artwork <br /><br />
                Up to <b>50</b> MB per Panels Project
              </p>
              <div class="pricing-button-wrapper">
                <p class="pricing-button">$100/month</p>
                <div class="sub-pricing-button">(Pricing in USD)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="pricing-2-container">
        <p>*One view is counted every time someone plays your AR animation.</p>
      </div>

      <div class="download-container" id="download">
        <h2 style="margin-bottom: 60px; text-align: center">
          Start Creating!
        </h2>
        <div class="form-download-buttons">
          <a href="/login" target="_top">
            <button id="form-download-button" class="primary">
              Launch Now
            </button>
          </a>
        </div>
        <p class="download-links">
          Or download legacy app:
          <a :href="macDownloadUrl || '#'" :aria-disabled="!macDownloadUrl" :class="{ 'disabled-link': !macDownloadUrl }" target="_blank" rel="noopener noreferrer">Mac</a>
          |
          <a :href="winDownloadUrl || '#'" :aria-disabled="!winDownloadUrl" :class="{ 'disabled-link': !winDownloadUrl }" target="_blank" rel="noopener noreferrer">Win</a>
        </p>
      </div>

      <div class="subscribe-section-container" id="news">
        <div class="subscribe-section-wrapper">
          <div class="subscribe-section-card">
            <h2 class="subscribe-section-title">Join our mailing list</h2>
            <p v-if="!isSubscribed" class="subscribe-section-subtitle">
              Stay up to date with new features, improvements and community news.
            </p>

            <form
              v-if="!isSubscribed"
              class="subscribe-section-form"
              novalidate
              @submit.prevent="submitSubscriptionForm"
            >
              <label for="subscribe-email">Email: *</label>
              <input
                id="subscribe-email"
                v-model.trim="subscriptionForm.email"
                :class="{ 'invalid-input': Boolean(subscriptionErrors.email) }"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                @input="clearSubscriptionError('email')"
              />
              <p v-if="subscriptionErrors.email" class="subscription-field-error">
                {{ subscriptionErrors.email }}
              </p>

              <label for="subscribe-name" class="subscribe-name-label">
                Name: (optional)
              </label>
              <input
                id="subscribe-name"
                v-model.trim="subscriptionForm.name"
                :class="{ 'invalid-input': Boolean(subscriptionErrors.name) }"
                type="text"
                autocomplete="name"
                placeholder="Jane Doe"
                @input="clearSubscriptionError('name')"
              />
              <p v-if="subscriptionErrors.name" class="subscription-field-error">
                {{ subscriptionErrors.name }}
              </p>

              <button
                id="subscribe-submit-button"
                class="primary"
                type="submit"
                :disabled="isSubmittingSubscription"
              >
                {{
                  isSubmittingSubscription
                    ? 'Submitting...'
                    : 'Subscribe'
                }}
              </button>
            </form>

            <p v-else class="subscription-status success">
              Subscribed. Please check your inbox for confirmation.
            </p>

            <p
              v-if="subscriptionStatusMessage && !isSubscribed"
              :class="['subscription-status', subscriptionStatusType]"
            >
              {{ subscriptionStatusMessage }}
            </p>
          </div>
        </div>
      </div>

      <!-- Articles teaser — the newest few articles, then through to /articles.
           Uses the same <ArticleCard> + .ej-card-grid as the /articles listing. -->
      <div
        v-if="featuredArticles.length"
        class="articles-section-container"
        id="articles"
      >
        <div class="articles-section-wrapper">
          <div class="articles-section-head">
            <h2 class="articles-section-title">Articles</h2>
            <p class="articles-section-subtitle">
              Guides, stories and updates on making augmented reality art — WebAR
              how-tos, artist features and practical tips.
            </p>
          </div>

          <div
            :class="['ej-card-grid', { 'ej-card-grid--center': featuredArticles.length < 3 }]"
          >
            <ArticleCard
              v-for="a in featuredArticles"
              :key="a.slug"
              :article="a"
            />
          </div>

          <div class="articles-section-cta">
            <router-link to="/articles/">
              <button class="primary">Browse all articles</button>
            </router-link>
          </div>
        </div>
      </div>

      <div class="FAQ-container" id="faq">
        <div class="faq-wrapper">
          <h2 class="FAQ-title">FAQs</h2>
          <div class="faqs">
            <FAQ
              v-for="(faq, i) in faqs"
              :faq="faq"
              :index="i"
              :key="i"
              :open="faq.open"
              @toggleOpen="toggleOpen"
            />
          </div>
        </div>
      </div>
  </div>
</template>

<script>
// Home view — the marketing page body. The shared header/footer and mobile-nav
// state live in the shell (App.vue); this view holds only home-specific content
// and logic (hero, steps, demo, panels, pricing, mailing form, FAQ).
import FAQ from '../components/FAQ'
import ArticleCard from '../components/ArticleCard.vue'
import { publishedArticles } from '../articles/registry'
import yaml from 'js-yaml'

export default {
  name: 'HomeView',
  components: {
    FAQ,
    ArticleCard
  },
  computed: {
    // Newest few articles teased on the home page; the rest live at /articles.
    featuredArticles () {
      return publishedArticles.slice(0, 3)
    }
  },
  data () {
    return {
      platform: '',
      form: {
        NAME: '',
        EMAIL: '',
        MMERGE4: ''
      },
      formMsg: '',
      nameIsValid: true,
      emailIsValid: true,
      isSubmittingSubscription: false,
      isSubscribed: false,
      subscriptionStatusType: '',
      subscriptionStatusMessage: '',
      subscriptionErrors: {
        email: '',
        name: ''
      },
      subscriptionForm: {
        email: '',
        name: ''
      },
      macDownloadUrl: '',
      winDownloadUrl: '',
      chosenPlatform: '',
      showAnswer: false,
      faqs: [
        {
          question: 'How does EyeJack Creator work?',
          answer:
            'EyeJack Creator is a simple web app that lets artists bring their art to life with animations and sound with three simple steps... <br>1) Upload your art. <br>2) Upload your animation. <br>3) Test your new AR art.',
          open: false
        },
        {
          question: 'How can I add an animation?',
          answer:
            'EyeJack Creator supports videos, animated gifs or png sequences. You could create a video using your smartphone OR if you want to try something more advanced, we recommend using programs like Adobe Photoshop, Animate or After Effects to create animations.',
          open: false
        },
        {
          question: 'Can I do animation with transparency?',
          answer:
            'Yes you can import your animation into EyeJack Creator as a transparent PNG sequence or a transparent GIF and it will show as transparent in AR!',
          open: false
        },
        {
          question: 'How long should an AR animation be?',
          answer:
            'We find that 30 second is a good amount of length for an AR animation. If you require your animation to be longer, an animation can be up to 60 seconds in length on the Artist plan and up to 120 seconds in length on the Virtuoso plan.',
          open: false
        },
        {
          question: 'Will my Artwork always be available?',
          answer:
            "Once you create an Artwork, it will always be available. If you're creating an Artwork for a commercial project, we recommend jumping onto the Artist plan so that you always have enough Views for your artwork. Please see the Pricing section for more info on Views.",
          open: false
        }
      ]
    }
  },
  methods: {
    toggleOpen: function (index) {
      this.faqs = this.faqs.map((faq, i) => {
        if (index === i) {
          faq.open = !faq.open
        } else {
          faq.open = false
        }
        return faq
      })
    },
    submit () {
      let url = document
        .getElementById('mc-embedded-subscribe-form')
        .getAttribute('action')
      url = url.replace('/post?u=', '/post-json?u=')
      url +=
        '&' +
        this.encode({
          ...this.form
        }) +
        '&c=displayMailchimpStatus'

      let script = window.document.createElement('script')
      script.src = url
      let ref = window.document.getElementsByTagName('script')[0]
      ref.parentNode.insertBefore(script, ref)

      script.onload = function () {
        this.remove()
      }
    },
    encode (data) {
      return Object.keys(data)
        .map(
          (key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`
        )
        .join('&')
    },
    checkPlatform () {
      let userAgent = navigator.userAgent || navigator.vendor || window.opera
      if (/Win/i.test(userAgent)) {
        this.platform = 'Windows'
        this.chosenPlatform = 'Windows'
      } else {
        this.platform = 'Mac'
        this.chosenPlatform = 'Mac'
      }
    },
    async fetchText (url) {
      const response = await fetch(url, { cache: 'no-store' })
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`)
      }
      return response.text()
    },
    async resolveDownloadUrls () {
      try {
        const [macYaml, winYaml] = await Promise.all([
          this.fetchText('https://s3.amazonaws.com/eyejack-creator-2020/prd/mac/latest-mac.yml'),
          this.fetchText('https://s3.amazonaws.com/eyejack-creator-2020/prd/win/latest.yml')
        ])
        const macFile = yaml
          .safeLoad(macYaml)
          .files.find((file) => file.url.includes('.dmg'))
          .url
          .replace(/ /g, '+')
        const winFile = yaml
          .safeLoad(winYaml)
          .path
          .replace(/ /g, '+')
        this.macDownloadUrl = `https://s3.amazonaws.com/eyejack-creator-2020/prd/mac/${macFile}`
        this.winDownloadUrl = `https://s3.amazonaws.com/eyejack-creator-2020/prd/win/${winFile}`
      } catch (error) {
        this.macDownloadUrl = ''
        this.winDownloadUrl = ''
      }
    },
    setChosenPlatform (platform) {
      this.chosenPlatform = platform
    },
    downloadMac () {
      if (!this.macDownloadUrl) return
      window.open(this.macDownloadUrl, '_blank', 'noopener,noreferrer')
    },
    downloadWin () {
      if (!this.winDownloadUrl) return
      window.open(this.winDownloadUrl, '_blank', 'noopener,noreferrer')
    },
    validateName () {
      // eslint-disable-next-line
      this.nameIsValid = !document.querySelector("#mce-NAME:invalid");
      if (document.querySelector('#mce-NAME:invalid')) {
        document.querySelector('#mce-NAME').classList.add('invalid-input')
      } else {
        document.querySelector('#mce-NAME').classList.remove('invalid-input')
      }
    },
    validateEmail () {
      // eslint-disable-next-line
      this.emailIsValid = !document.querySelector("#mce-EMAIL:invalid");
      if (document.querySelector('#mce-EMAIL:invalid')) {
        document.querySelector('#mce-EMAIL').classList.add('invalid-input')
      } else {
        document.querySelector('#mce-EMAIL').classList.remove('invalid-input')
      }
    },
    getSubscriptionEndpoint () {
      const host = window.location.hostname
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:8080/subscribe-creator'
      }
      return 'https://api.mail.eyejack.io/subscribe-creator'
    },
    getSignupSource () {
      try {
        const fullPath = this.$route && this.$route.fullPath
        if (fullPath) {
          return new URL(fullPath, window.location.origin).toString()
        }
        const { origin, pathname, search } = window.location
        return `${origin}${pathname}${search}`
      } catch (error) {
        return window.location.href || ''
      }
    },
    validateSubscriptionForm () {
      this.subscriptionErrors.email = ''
      this.subscriptionErrors.name = ''

      const email = this.subscriptionForm.email || ''
      const name = this.subscriptionForm.name || ''
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

      if (!emailRegex.test(email)) {
        this.subscriptionErrors.email = 'Please enter a valid email address.'
      }

      if (name.length > 0) {
        const compactName = name.replace(/\s+/g, ' ').trim()
        const hasLetter = /[A-Za-z]/.test(compactName)
        if (compactName.length < 2 || !hasLetter) {
          this.subscriptionErrors.name = 'Please enter a valid name.'
        }
      }

      return !this.subscriptionErrors.email && !this.subscriptionErrors.name
    },
    clearSubscriptionError (fieldName) {
      if (this.subscriptionErrors[fieldName]) {
        this.subscriptionErrors[fieldName] = ''
      }
    },
    async submitSubscriptionForm () {
      if (!this.validateSubscriptionForm()) {
        this.subscriptionStatusType = 'error'
        this.subscriptionStatusMessage = 'Please correct the highlighted fields.'
        return
      }

      this.isSubmittingSubscription = true
      this.subscriptionStatusType = ''
      this.subscriptionStatusMessage = ''

      try {
        const payload = {
          email: this.subscriptionForm.email,
          name: this.subscriptionForm.name || undefined,
          signup_source: this.getSignupSource()
        }

        const response = await fetch(this.getSubscriptionEndpoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })

        const body = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(body.error || body.message || 'Unable to submit subscription.')
        }

        this.subscriptionStatusType = 'success'
        this.subscriptionStatusMessage = 'Subscribed. Please check your inbox for confirmation.'
        this.isSubscribed = true
        this.subscriptionForm.email = ''
        this.subscriptionForm.name = ''
      } catch (error) {
        this.subscriptionStatusType = 'error'
        this.subscriptionStatusMessage = (error && error.message) || 'Submission failed. Please try again.'
      } finally {
        this.isSubmittingSubscription = false
      }
    }
  },
  created () {
    this.resolveDownloadUrls()

    let vimeoScript = document.createElement('script')
    vimeoScript.setAttribute('src', 'https://player.vimeo.com/api/player.js')
    document.head.appendChild(vimeoScript)

    window.displayMailchimpStatus = (data) => {
      if (!data.result || !data.msg) return
      let mcStatus = document.querySelector('.mc-status')
      if (!mcStatus) return
      mcStatus.setAttribute('tabindex', '-1')
      mcStatus.focus()

      // console.log(data)

      if (data.result !== 'error' || data.msg.includes('already subscribed')) {
        this.formMsg = 'Thanks!'
        if (this.chosenPlatform === 'Mac') {
          this.downloadMac()
        } else {
          this.downloadWin()
        }
      } else {
        this.formMsg = 'Something went wrong!'
      }
    }
  },
  mounted () {
    this.checkPlatform()
  }
}
</script>
